// /inventory · 当日现货 — institutional-grade redesign.
//
// Replaces the previous bulky card-feed with a dense, scannable spreadsheet:
//   - Sticky toolbar (search + sort + segmented shift toggle + Cmd+K)
//   - Compact KPI bar (3 inline stats with delta indicators)
//   - Slim daily-target progress strip (only when shift is day or night)
//   - Airtable-style grid: 1 row per SKU × 4 location cols + today-delta
//   - Click a stock cell → inline popover to log event (2 taps total)
//   - Click SKU name → slide-over with full balances + activity + trend link
//   - Cmd+K (or /) → command palette: "草莓 RH 售出 10" → enter
//   - Mobile (< 768px) collapses to a compact list + bottom drawer
//
// All visual rules in src/styles/inventory-grid.css. Zero inline style={{}}.

import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api.ts';
import { navigate } from '../lib/routing.js';
import { Toast } from '../components/atoms.jsx';
import { InventoryGrid } from '../components/InventoryGrid.jsx';
import { InventorySlideOver } from '../components/InventorySlideOver.jsx';
import { CommandPalette } from '../components/CommandPalette.jsx';
import { CustomizeOrderModal } from '../components/CustomizeOrderModal.jsx';
import { Icon } from '../components/Icon.jsx';
import { DailyTargetModal } from '../components/DailyTargetModal.jsx';
import { ConfirmLogModal } from '../components/ConfirmLogModal.jsx';
import { ActivityFeedCompact } from '../components/ActivityFeed.jsx';
import { CertifyStrip } from '../components/CertifyStrip.jsx';
import { LOCATIONS } from '../lib/locations.ts';
import { isElevated } from '../lib/auth.ts';
import { useConfirmGate } from '../lib/useConfirmGate.js';
import { torontoToday, torontoMidnightUtcIso, torontoEndExclusiveUtcIso } from '../lib/torontoTime.js';

// localStorage key — shift selection persists across visits (per shift handoff).
const SHIFT_STORAGE_KEY = 'vava.inventory.shift';
// Per-session working day (sessionStorage, elevated users only). Persisting it
// means a mid-session reload / tab-switch / PWA-wake AFTER midnight restores the
// day she was working on instead of silently jumping to the new ET day — which
// is how late-night close-out entries used to land on the wrong day (Karen,
// 2026-06-04). Only today/yesterday are restored, so a stale value can't strand
// her on an old day; the off-today banner + 回到今日 handle the morning case.
const WORKING_DAY_KEY = 'vava.inventory.working_day';

// Decide the working day to open on, given a persisted value + today (ET).
// Restore only today or yesterday (the midnight-rollover case); anything older
// or malformed/future falls back to today so a stale value never strands the
// user on an old day. Pure + exported for tests.
export function resolveInitialWorkingDay(saved, todayET) {
  if (!saved || !/^\d{4}-\d{2}-\d{2}$/.test(saved) || saved > todayET) return todayET;
  const y = new Date(todayET); y.setUTCDate(y.getUTCDate() - 1);
  const yesterday = y.toISOString().slice(0, 10);
  return (saved === todayET || saved === yesterday) ? saved : todayET;
}
function loadInitialShift() {
  try {
    const s = localStorage.getItem(SHIFT_STORAGE_KEY);
    if (s === 'day' || s === 'night' || s === 'all') return s;
  } catch { /* localStorage may be disabled */ }
  return 'all';
}

const SORT_OPTIONS = [
  { key: 'category',       label: '按品类' },
  { key: 'name',           label: '按名称' },
  { key: 'total',          label: '按总库存' },
  { key: 'today_produced', label: '今日生产' },
  { key: 'today_sold',     label: '今日售出' },     // added — completes the kind triad
  { key: 'today_wasted',   label: '今日报废' }
];


const SHIFT_OPTIONS = [
  { key: 'day',   label: '☀ 白班' },
  { key: 'night', label: '🌙 晚班' },
  { key: 'all',   label: '全部' }
];

export const Inventory = ({ user }) => {
  // ---- core data state
  const [shift, setShift] = useState(loadInitialShift);
  const [grid, setGrid] = useState(null);
  const [thresholds, setThresholds] = useState([]);
  const [staff, setStaff] = useState([]);          // for the 店员取走 picker
  const [customTakers, setCustomTakers] = useState([]);   // recalled non-staff names
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [todayTarget, setTodayTarget] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  // ---- UI state
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('category');
  const [lowOnly, setLowOnly] = useState(false);   // 低库存 filter toggle
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  // Per-user pinned product order (ids, in display order). Pinned products float
  // to the top of the grid; loaded once from the server (follows the account).
  const [customOrder, setCustomOrder] = useState([]);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [detailProductId, setDetailProductId] = useState(null);
  const [toast, setToast] = useState(null);

  // ---- confirm-quantity gate (T6). Every commit path (cell popover, Cmd+K)
  // funnels through one promise-gated modal so the qty is echoed before it
  // lands. Single-flight: a second entry can't overwrite the pending resolver.
  const gate = useConfirmGate();

  // ---- past-day data entry (T8). Default today; admin/店长 may pick a past ET
  // day to view point-in-time stock + backfill. Regular staff stay on today.
  const todayET = torontoToday();
  // Restore the session's working day (elevated only) so crossing midnight
  // mid-session doesn't silently advance the day under her. Only today/yesterday
  // are honored — a stale value falls back to today.
  const [selectedDate, setSelectedDate] = useState(() => {
    if (!isElevated(user)) return todayET;
    try {
      return resolveInitialWorkingDay(sessionStorage.getItem(WORKING_DAY_KEY), todayET);
    } catch { return todayET; }   // sessionStorage unavailable
  });
  // Persist the working day for the session (elevated only).
  useEffect(() => {
    if (!isElevated(user)) return;
    try { sessionStorage.setItem(WORKING_DAY_KEY, selectedDate); } catch { /* ignore */ }
  }, [selectedDate, user]);
  const [dayDeltas, setDayDeltas] = useState(null);   // Map<productId,{produced,sold,wasted}>
  const [cellDeltaMap, setCellDeltaMap] = useState(null);  // Map<"productId|location",{plus,minus}>
  const isToday = selectedDate === todayET;
  const canBackfill = isElevated(user);

  const todayYMD = new Date().toISOString().slice(0, 10);

  async function refresh(shiftArg = shift, dateArg = selectedDate) {
    setRefreshing(true);
    try {
      const onToday = dateArg === todayET;
      // Point-in-time balances as of end of the selected ET day (live when today).
      const asOfUtc = onToday ? undefined : torontoEndExclusiveUtcIso(dateArg);
      const dayFromUtc = torontoMidnightUtcIso(dateArg);
      const dayToUtc   = torontoEndExclusiveUtcIso(dateArg);
      const gridOpts = shiftArg === 'all' ? {} : { shift: shiftArg };
      if (asOfUtc) gridOpts.asOfUtc = asOfUtc;
      const shiftOpt = (shiftArg === 'day' || shiftArg === 'night') ? { shift: shiftArg } : {};
      const targetPromise = (onToday && (shiftArg === 'day' || shiftArg === 'night'))
        ? api.loadDailyTargets(todayYMD, todayYMD).catch(() => [])
        : Promise.resolve([]);
      const [list, ts, st, ev, dayStats, cells, targets] = await Promise.all([
        api.loadInventoryGrid(gridOpts),
        api.listThresholds().catch(() => []),
        // 今日/7天/30天 anchored to the SELECTED ET day so the KPI card matches
        // the grid's per-day 今日变动 (and 今日 doesn't bleed past ET midnight).
        api.loadInventoryStats(dateArg).catch(() => null),
        // Feed: live recent-100 on today; scoped to the selected ET day when
        // viewing history, so it matches the grid's per-day balances (Karen
        // 2026-06-04: a global feed on a historical-day grid reads as "the
        // transfer/sale didn't happen" when it just happened on a later day).
        (onToday
          ? api.loadInventoryEvents(100)
          : api.loadInventoryEvents(100, { fromUtc: dayFromUtc, toUtc: dayToUtc })
        ).catch(() => []),
        // Server-aggregated per-SKU deltas for the selected ET day (accurate,
        // occurred_at-windowed — replaces the old client-side 100-event scan).
        api.loadInventoryStatsByProduct(dayFromUtc, dayToUtc, { utc: true, ...shiftOpt }).catch(() => null),
        // Per-(SKU, location) +/− for the selected ET day — drives the in-cell
        // "+12 −12" second line. Server-windowed by occurred_at so it works on
        // any 补录 day (the old client scan only ever showed today's).
        api.loadCellDeltas(dayFromUtc, dayToUtc).catch(() => []),
        targetPromise
      ]);
      setGrid(list);
      setThresholds(ts);
      setStats(st);
      setActivity(ev);
      const dd = new Map();
      for (const r of (dayStats?.rows || [])) {
        dd.set(r.id, { produced: r.produced, sold: r.sold, wasted: r.wasted });
      }
      setDayDeltas(dd);
      const cm = new Map();
      for (const r of (cells || [])) {
        cm.set(`${r.productId}|${r.location}`, { plus: r.plus, minus: r.minus });
      }
      setCellDeltaMap(cm);
      const t = (targets || []).find(x => x.date === todayYMD && x.shift === shiftArg);
      setTodayTarget(t ? { targetUnits: t.targetUnits, targetText: t.targetText } : null);
      setErr('');
    } catch (e) {
      setErr(e?.message || '加载失败');
    } finally {
      setRefreshing(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  // Active staff for the 店员取走 picker — loaded once (cheap, rarely changes).
  useEffect(() => {
    api.listStaff()
      .then(list => setStaff((list || []).filter(s => s.active !== false)))
      .catch(() => setStaff([]));
  }, []);

  // Recalled custom takers (non-staff names typed previously) for the same picker.
  // Reloaded after a custom submit so a just-typed name appears immediately.
  function loadCustomTakers() {
    api.listCustomTakers()
      .then(list => setCustomTakers(list || []))
      .catch(() => setCustomTakers([]));
  }
  useEffect(() => { loadCustomTakers(); /* eslint-disable-line */ }, []);

  // My personal product order — loaded once (follows the account).
  useEffect(() => {
    api.getMyInventoryOrder()
      .then(ids => setCustomOrder(ids || []))
      .catch(() => setCustomOrder([]));
  }, []);

  async function saveCustomOrder(productIds) {
    const saved = await api.saveMyInventoryOrder(productIds);
    setCustomOrder(saved);
    setToast({ kind: 'success', msg: saved.length ? `已置顶 ${saved.length} 个常用产品` : '已清空置顶' });
  }

  // Global Cmd+K / Ctrl+K / "/" → open command palette.
  useEffect(() => {
    function onKey(e) {
      // Avoid hijacking shortcuts while the user is typing in an input/textarea.
      const tag = (e.target?.tagName || '').toLowerCase();
      const inField = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault(); setPaletteOpen(true);
      } else if (e.key === '/' && !inField) {
        e.preventDefault(); setPaletteOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function changeShift(newShift) {
    setShift(newShift);
    try { localStorage.setItem(SHIFT_STORAGE_KEY, newShift); } catch { /* ignore */ }
    refresh(newShift, selectedDate);
  }

  // Switch the working day. Clamped to ≤ today by the picker; only rendered for
  // admin/店长 (regular staff stay on today).
  function changeDate(ymd) {
    const next = ymd > todayET ? todayET : ymd;
    setSelectedDate(next);
    refresh(shift, next);
  }

  // Backfill instant for a past day = noon of that ET day (safely inside the
  // day's window). undefined when today → server uses now().
  function occurredAtForSelected() {
    if (selectedDate === todayET) return undefined;
    const start = new Date(torontoMidnightUtcIso(selectedDate)).getTime();
    return new Date(start + 12 * 3600 * 1000).toISOString();
  }

  async function saveDailyTarget(units, text) {
    await api.setDailyTarget(todayYMD, shift, units, text);
    await refresh(shift);
    setToast({ kind: 'success', msg: '已更新今日目标' });
  }

  // Commit handlers passed to the grid/palette. They DON'T write directly —
  // they request confirmation and return the gate's promise. The grid/palette
  // keep awaiting exactly as before; the promise just resolves later (after the
  // user confirms) or rejects (on cancel → caller shows a failure + stays open).
  function logEvent({ productId, kind, location, qty, reason, taken_by, taken_by_custom_id, new_taker_name, takenByName, note }) {
    return gate.request({ type: 'log', productId, kind, location, qty, reason, taken_by, taken_by_custom_id, new_taker_name, takenByName, note });
  }
  function transfer({ productId, from, to, qty }) {
    return gate.request({ type: 'transfer', productId, from, to, qty });
  }

  // 改写 (override) — 店长以上 set an absolute quantity at a (product,location).
  // Server computes the atomic delta and appends an `adjusted` audit event.
  // No confirm gate (the popover already shows current→target preview + reason).
  async function adjust({ productId, location, target, reason, note }) {
    const res = await api.adjustInventory({ product_id: productId, location, target, reason, note });
    setToast({
      kind: 'success',
      msg: res?.noop ? '库存未变（目标与当前相同）' : `已改写 · ${location} 改为 ${target}`
    });
    await refresh(shift, selectedDate);
    return res;
  }

  // 撤销生产 — 店长以上 reverse a production that did not happen. Appends a
  // negative `produced` event so 生产/损耗率 net down (改写 cannot). Audited.
  async function reverseProduce({ productId, location, qty, note }) {
    const res = await api.reverseProduction({ product_id: productId, location, qty, note });
    setToast({ kind: 'success', msg: `已撤销生产 · ${location} −${qty}` });
    await refresh(shift, selectedDate);
    return res;
  }

  // Show an undoable toast after a fresh log. 撤回 hard-deletes the event within
  // the 5-minute window (no trace). Only offered for today's live entries.
  function offerUndo(event, verb, qty, location, dateTag) {
    if (!event?.id) {
      setToast({ kind: 'success', msg: `已记录 · ${verb} ${qty} @ ${location}${dateTag}` });
      return;
    }
    setToast({
      kind: 'success',
      msg: `已记录 · ${verb} ${qty} @ ${location}${dateTag}`,
      duration: 7000,
      action: {
        label: '撤回',
        onClick: async () => {
          try {
            await api.undoInventoryEvent(event.id);
            setToast({ kind: 'success', msg: `已撤回 · ${verb} ${qty} @ ${location}` });
            await refresh(shift, selectedDate);
          } catch (e) {
            setToast({ kind: 'error', msg: e?.message || '撤回失败（可能已超过 5 分钟）' });
          }
        }
      }
    });
  }

  // Runs when the confirm modal's 确认 is pressed: performs the real write for
  // whatever payload is pending, then resolves/rejects the gate's promise.
  function confirmPending() {
    return gate.runConfirm(async (payload) => {
      const occurred_at = occurredAtForSelected();
      const dateTag = isToday ? '' : ` · ${selectedDate}`;
      if (payload.type === 'log') {
        const { productId, kind, location, qty, reason, taken_by, taken_by_custom_id, new_taker_name, note } = payload;
        const event = await api.logInventoryEvent({
          kind, product_id: productId, location, qty,
          ...(reason ? { reason } : {}),
          ...(taken_by ? { taken_by } : {}),
          ...(taken_by_custom_id ? { taken_by_custom_id } : {}),
          ...(new_taker_name ? { new_taker_name } : {}),
          ...(note ? { note } : {}),
          ...(occurred_at ? { occurred_at } : {})
        });
        // A freshly typed custom taker may now be recallable — refresh the list.
        if (new_taker_name) loadCustomTakers();
        const verb = kind === 'sold' ? '售出' : kind === 'wasted' ? '报废' : kind === 'produced' ? '生产' : '调整';
        // Offer a 5-min 撤回 for fresh, today entries; static toast for backfill.
        if (isToday) offerUndo(event, verb, qty, location, dateTag);
        else setToast({ kind: 'success', msg: `已记录 · ${verb} ${qty} @ ${location}${dateTag}` });
        await refresh(shift, selectedDate);
        return event;
      }
      const { productId, from, to, qty } = payload;
      await api.transferInventory({
        product_id: productId, from_location: from, to_location: to, qty,
        ...(occurred_at ? { occurred_at } : {})
      });
      setToast({ kind: 'success', msg: `已调拨 ${qty} · ${from} → ${to}${dateTag}` });
      await refresh(shift, selectedDate);
    });
  }

  // 重设该日 (T4) — clear the selected SKU's data for the active ET day. Admin/
  // 店长 only (gated server-side too). Reverts the day's deltas; balance returns
  // to the prior carryover, not zero.
  async function resetDay(productId) {
    const res = await api.resetProductDay(productId, selectedDate);
    const t = res.cleared?.totals;
    const parts = [];
    if (t?.produced) parts.push(`生产${t.produced}`);
    if (t?.sold)     parts.push(`售出${t.sold}`);
    if (t?.wasted)   parts.push(`报废${t.wasted}`);
    const summary = parts.length ? parts.join('/') : `${res.deleted} 条`;
    setToast({ kind: 'success', msg: `已重设 ${selectedDate} · 清除 ${summary}` });
    await refresh(shift, selectedDate);
    return res;
  }

  // 重设库存 — zero the SKU's current stock everywhere via compensating
  // adjustments (history preserved). Admin/店长 only (gated server-side too).
  async function resetStock(productId) {
    const res = await api.resetProductStock(productId);
    setToast({ kind: 'success', msg: res.count > 0 ? `已清零库存 · ${res.count} 个地点` : '库存已为零' });
    await refresh(shift, selectedDate);
    return res;
  }

  // Index thresholds for O(1) lookup by (productId, location).
  const thresholdMap = useMemo(
    () => new Map(thresholds.map(t => [`${t.productId}|${t.location}`, t.threshold])),
    [thresholds]
  );
  // Low-stock count for the toolbar badge.
  const lowCount = useMemo(() => {
    if (!grid) return 0;
    let n = 0;
    for (const p of grid) {
      for (const loc of LOCATIONS) {
        const th = thresholdMap.get(`${p.id}|${loc}`);
        if (th !== undefined && (p.balances[loc] || 0) < th) n++;
      }
    }
    return n;
  }, [grid, thresholdMap]);

  // Today's produced (for target % — uses stats endpoint for accuracy).
  const producedToday = stats?.today?.produced ?? 0;
  const attainmentPct = todayTarget && todayTarget.targetUnits > 0
    ? Math.round((producedToday / todayTarget.targetUnits) * 100)
    : null;

  const detailProduct = detailProductId
    ? (grid || []).find(p => p.id === detailProductId)
    : null;

  return (
    <>
      {/* ===== Page header ===== */}
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">{isToday ? '当日现货' : `历史现货 · ${selectedDate}`}</h1>
          <div className="vava-page-meta">
            {grid === null
              ? '加载中…'
              : `${grid.length} 个 SKU${shift === 'day' ? ' · 白班' : shift === 'night' ? ' · 晚班' : ''}${lowCount ? ` · ${lowCount} 项低于阈值` : ''}`}
          </div>
        </div>
        <div className="inv-page-actions">
          {/* Date roll — admin/店长 only. Pick a past ET day to view its
              end-of-day stock and backfill/correct that day's data. No future. */}
          {canBackfill && (
            <div className="inv-date-roll">
              <button className="btn btn-secondary inv-icon-btn" aria-label="前一天"
                      title="前一天" disabled={refreshing}
                      onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); changeDate(d.toISOString().slice(0,10)); }}>‹</button>
              <input type="date" className="vava-input inv-date-input"
                     value={selectedDate} max={todayET} disabled={refreshing}
                     onChange={(e) => e.target.value && changeDate(e.target.value)} />
              <button className="btn btn-secondary inv-icon-btn" aria-label="后一天"
                      title="后一天" disabled={refreshing || isToday}
                      onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); changeDate(d.toISOString().slice(0,10)); }}>›</button>
              {!isToday && (
                <button className="btn btn-secondary" disabled={refreshing}
                        onClick={() => changeDate(todayET)}>今日</button>
              )}
            </div>
          )}
          <button className="btn btn-secondary inv-icon-btn"
                  onClick={() => refresh()} disabled={refreshing}
                  title={refreshing ? '刷新中…' : '刷新'}
                  aria-label="刷新">
            {refreshing ? '⟳' : '↻'}
          </button>
          <button className="btn btn-primary"
                  onClick={() => navigate('/inventory/stats')}>
            📊 统计报表
          </button>
        </div>
      </div>

      {/* Off-today banner — make it unmistakable you're editing a past day. */}
      {!isToday && (
        <div className="att-warning inv-info-banner" role="status">
          <strong>正在查看 / 补录 {selectedDate} 的数据</strong> · 库存为该日结存（截至当日 24:00）·
          新增记录将记到该日 · <button className="inv-link-btn" onClick={() => changeDate(todayET)}>回到今日</button>
        </div>
      )}

      {/* ===== 库存认证 strip — settle the selected day per store (店长+).
              Data entry stays in the grid below; this only stamps the day so
              备货准确率 has a stable actual. Driven by the date roll above. ===== */}
      <CertifyStrip
        selectedDate={selectedDate}
        elevated={canBackfill}
        onJumpToDate={changeDate}
        onToast={setToast} />

      {/* ===== Sticky toolbar (search + sort + shift toggle) ===== */}
      <div className="inv-toolbar">
        <div className="inv-segmented" role="tablist" aria-label="班次视图">
          {SHIFT_OPTIONS.map(o => (
            <button key={o.key}
                    role="tab"
                    aria-selected={shift === o.key}
                    onClick={() => changeShift(o.key)}
                    disabled={refreshing && shift !== o.key}>
              {o.label}
            </button>
          ))}
        </div>
        <div className="inv-search">
          <input type="text"
                 value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="搜 SKU…（草莓 / 6寸 / 胚）" />
          <span className="inv-search-kbd">/</span>
        </div>
        <select className="inv-sort"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                aria-label="排序">
          {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        {lowCount > 0 && (
          <button className={`inv-low-chip ${lowOnly ? 'active' : ''}`}
                  aria-pressed={lowOnly}
                  onClick={() => setLowOnly(v => !v)}
                  title={lowOnly ? '显示全部产品' : '只看低于阈值的产品'}>
            ⚠ {lowCount} 低库存
          </button>
        )}
        <button className="btn btn-secondary"
                onClick={() => setPaletteOpen(true)}
                title="命令面板 (⌘K)">⌘K 录入</button>
        <button className="btn btn-secondary inv-customize-btn"
                onClick={() => setCustomizeOpen(true)}
                disabled={!grid}
                title="自定义产品顺序 · 把常用产品置顶">
          <Icon name="sliders" />自定义
        </button>
      </div>

      {/* ===== Error banner (post-load only) ===== */}
      {err && grid !== null && (
        <div className="att-error inv-error" role="alert">{err}</div>
      )}

      {/* ===== Daily target progress strip (today only — targets are today-scoped) ===== */}
      {isToday && (shift === 'day' || shift === 'night') && (
        <TargetStrip
          shift={shift}
          target={todayTarget}
          producedToday={producedToday}
          attainmentPct={attainmentPct}
          onEdit={() => setTargetModalOpen(true)} />
      )}

      {/* ===== KPI bar — today's rolling totals, or the selected day's totals
              when viewing history (so a past day isn't headline-number-blind). ===== */}
      {isToday ? <KpiBar stats={stats} /> : <PastDayKpiBar dayDeltas={dayDeltas} date={selectedDate} />}

      {/* ===== Main grid ===== */}
      <InventoryGrid
        products={grid}
        thresholdMap={thresholdMap}
        recentEvents={activity}
        dayDeltas={dayDeltas}
        cellDeltaMap={cellDeltaMap}
        deltaLabel={isToday ? '今日变动' : '当日变动'}
        search={search}
        sortKey={sortKey}
        lowOnly={lowOnly}
        customOrder={customOrder}
        staff={staff}
        customTakers={customTakers}
        onLogEvent={logEvent}
        onTransfer={transfer}
        onAdjust={adjust}
        onReverseProduce={reverseProduce}
        canAdjust={canBackfill}
        onOpenDetail={setDetailProductId} />

      {/* ===== Compact activity feed ===== */}
      {/* Last 5 events with "查看全部" link to /inventory/activity. Regina
          asked for the "Regina 报废了 N 个 草莓 @ RH" feed to be visible on
          the daily-loop page — this is the lightweight version. */}
      <ActivityFeedCompact
        events={activity}
        title={isToday ? '最近活动' : `${selectedDate} 当日活动`}
        emptyHint={isToday ? undefined : `${selectedDate} 这一天没有库存活动`} />

      {/* ===== Modals + overlays ===== */}
      {targetModalOpen && (shift === 'day' || shift === 'night') && (
        <DailyTargetModal
          shift={shift}
          date={todayYMD}
          current={todayTarget}
          onSave={saveDailyTarget}
          onClose={() => setTargetModalOpen(false)} />
      )}
      {detailProduct && (
        <InventorySlideOver
          product={detailProduct}
          user={user}
          selectedDate={selectedDate}
          isToday={isToday}
          onClose={() => setDetailProductId(null)}
          onLogEvent={logEvent}
          onTransfer={transfer}
          onReset={resetDay}
          onResetStock={resetStock} />
      )}
      {paletteOpen && (
        <CommandPalette
          products={grid || []}
          onLog={logEvent}
          onTransfer={transfer}
          onOpenDetail={setDetailProductId}
          onClose={() => setPaletteOpen(false)} />
      )}
      {customizeOpen && (
        <CustomizeOrderModal
          products={grid || []}
          initialOrder={customOrder}
          onSave={saveCustomOrder}
          onClose={() => setCustomizeOpen(false)} />
      )}
      {gate.pending && (
        <ConfirmLogModal
          pending={gate.pending}
          products={grid || []}
          selectedDate={selectedDate}
          isToday={isToday}
          onConfirm={confirmPending}
          onCancel={gate.cancel} />
      )}
      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </>
  );
};

// ============================================================================
// KpiBar — compact horizontal stats (今日 / 7天 / 30天 → 生产 / 售出 / 报废)
// ============================================================================

const KpiBar = ({ stats }) => {
  // Compact 3-card row showing today's headline numbers + this week/month
  // as secondary text below. Replaces the old 3×3 number grid.
  if (!stats) {
    return (
      <div className="inv-kpis">
        {['生产', '售出', '报废'].map((label, i) => (
          <div key={i} className="inv-kpi">
            <span className="inv-kpi-label">{label}</span>
            <span className="inv-skeleton w-md" />
            <span className="inv-skeleton w-sm" />
          </div>
        ))}
      </div>
    );
  }
  const cells = [
    { key: 'produced', label: '今日 生产', cls: 'produced' },
    { key: 'sold',     label: '今日 售出', cls: 'sold' },
    { key: 'wasted',   label: '今日 报废', cls: 'wasted' }
  ];
  // 在手 = current total stock across all products (point-in-time). It's NOT a
  // window sum — every 今日/7天/30天 "剩余" would equal this same number — so it
  // shows once, no 7天/30天 sub-line (Regina 2026-06-06).
  const onHand = stats.onHand ?? 0;
  return (
    <div className="inv-kpis">
      {cells.map(c => {
        let today = stats.today?.[c.key] ?? 0;
        let week  = stats.week?.[c.key]  ?? 0;
        let month = stats.month?.[c.key] ?? 0;
        let revNote = null;
        // 生产: show GROSS output (net + 撤销). A 撤销生产 stores a negative
        // `produced` row, so net can go ≤ 0 on a day with no fresh production —
        // which read as "生产 −6". Gross never goes negative; the reversal is
        // called out separately so the headline isn't misleading.
        if (c.key === 'produced') {
          today += stats.today?.reversed ?? 0;
          week  += stats.week?.reversed  ?? 0;
          month += stats.month?.reversed ?? 0;
          const rev = stats.today?.reversed ?? 0;
          if (rev > 0) revNote = `含撤销 −${rev}`;
        }
        return (
          <div key={c.key} className="inv-kpi">
            <span className="inv-kpi-label">{c.label}</span>
            <span className={`inv-kpi-value ${today ? c.cls : 'muted'}`}>{today}</span>
            <span className="inv-kpi-sub">7天 {week} · 30天 {month}</span>
            {revNote && <span className="inv-kpi-sub inv-kpi-rev">{revNote}</span>}
          </div>
        );
      })}
      <div className="inv-kpi">
        <span className="inv-kpi-label">当前 在手</span>
        <span className={`inv-kpi-value ${onHand ? 'remaining' : 'muted'}`}>{onHand}</span>
        <span className="inv-kpi-sub">所有产品当前库存</span>
      </div>
    </div>
  );
};

// ============================================================================
// PastDayKpiBar — the selected day's 生产/售出/报废 totals (summed from the
// per-SKU day aggregate already fetched for the grid). Shown instead of the
// live KPI bar when viewing a historical day, so a past day still has headline
// numbers (Regina 2026-06-06). No 在手 card — that's point-in-time and the grid
// already shows the day's end-of-day balances per cell.
// ============================================================================

const PastDayKpiBar = ({ dayDeltas, date }) => {
  if (!dayDeltas) {
    return (
      <div className="inv-kpis">
        {['生产', '售出', '报废'].map((label, i) => (
          <div key={i} className="inv-kpi">
            <span className="inv-kpi-label">{date} {label}</span>
            <span className="inv-skeleton w-md" />
          </div>
        ))}
      </div>
    );
  }
  let produced = 0, sold = 0, wasted = 0;
  for (const v of dayDeltas.values()) {
    produced += v.produced || 0; sold += v.sold || 0; wasted += v.wasted || 0;
  }
  const cells = [
    { label: '生产', val: produced, cls: 'produced' },
    { label: '售出', val: sold,     cls: 'sold' },
    { label: '报废', val: wasted,   cls: 'wasted' }
  ];
  return (
    <div className="inv-kpis">
      {cells.map(c => (
        <div key={c.label} className="inv-kpi">
          <span className="inv-kpi-label">{date} {c.label}</span>
          <span className={`inv-kpi-value ${c.val ? c.cls : 'muted'}`}>{c.val}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// TargetStrip — slim progress bar replacing the bulky daily-target card.
// ============================================================================

const TargetStrip = ({ shift, target, producedToday, attainmentPct, onEdit }) => {
  const shiftLabel = shift === 'day' ? '白班' : '晚班';
  if (!target) {
    return (
      <div className="inv-target">
        <span className="inv-target-label">{shiftLabel}今日目标</span>
        <span className="inv-target-empty">— 设定后即可追踪 达成率</span>
        <button className="btn btn-secondary" onClick={onEdit}>设定目标</button>
      </div>
    );
  }
  const pct = Math.min(100, attainmentPct ?? 0);
  const cls = pct >= 100 ? '' : pct >= 80 ? 'warn' : 'under';
  return (
    <div className="inv-target">
      <span className="inv-target-label">{shiftLabel}今日目标</span>
      <span className="inv-target-text">{producedToday} / {target.targetUnits}</span>
      <div className="inv-target-bar" role="progressbar"
           aria-valuenow={attainmentPct ?? 0} aria-valuemin={0} aria-valuemax={100}>
        <div className={`inv-target-fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="inv-target-pct">{attainmentPct}%</span>
      {target.targetText && (
        <span className="inv-target-label" title={target.targetText}>· {target.targetText}</span>
      )}
      <button className="btn btn-secondary" onClick={onEdit}>修改</button>
    </div>
  );
};
